import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import theme, { Theme } from 'helpers/theme';
import PageTitle from 'components/PageTitle';
import Table, { RowCell } from 'components/Table';
import useTableSearch from 'components/Table/useTableSearch';
import UserLabel from 'sharedComponents/core/Table/UserLabel';
import Text from 'sharedComponents/core/Table/Text/Text';
import { createFragmentContainer, graphql } from 'react-relay';
import { Router, useRouter } from 'found';
import { DelegatesPage_delegates } from './__generated__/DelegatesPage_delegates.graphql';
import { DelegatesPage_manager } from './__generated__/DelegatesPage_manager.graphql';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import DelegatesFilterOptions from 'components/Delegate/DelegatesFilterOptions';
import DelegatesSortOptions from 'components/Delegate/DelegatesSortOptions';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '40px 40px'
  },
  spacer: {
    marginBottom: 60
  },
  headerSide: {
    display: 'flex',
    alignItems: 'center'
  },
  headerDivider: {
    width: 1,
    height: 40,
    background: theme.colors.borderGrey,
    margin: [0, theme.spacing(3)]
  },
  headerSideCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gridGap: 6
  },
  headerSideText: {
    margin: 0,
    fontSize: theme.fontSizes.default,
    fontWeight: 700
  },
  downloadReport: {
    padding: '4px 10px',
    // Outdent by the pill's padding + border so its label text aligns
    // with the members-total text above
    marginLeft: -11,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 500,
    color: '#74777f',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3',
      color: theme.colors.primaryBlack
    }
  }
}));

type Props = {
  delegates: DelegatesPage_delegates;
  manager: DelegatesPage_manager;
  router: Router;
};

type DelegateRow = NonNullable<DelegatesPage_delegates['edges']>[number];

export const ALL_COMPLETED = 'allCompleted';
export const EXPIRING_BEFORE_6 = 'expiringBefore6';
export const EXPIRING_BEFORE_3 = 'expiringBefore3';
export const HAS_INCOMPLETE_COURSE = 'incompleteCourse';
export const HAS_ACTIVE_DOCUMENT = 'hasActiveDocument';
export const HAS_EXPIRED_DOCUMENT = 'hasExpiredDocument';
export const HAS_ACTIVE_BACKGROUND_CHECK = 'hasActiveBackgroundCheck';
export const HAS_EXPIRED_BACKGROUND_CHECK = 'hasExpiredBackgroundCheck';

const DelegatesPage = ({ delegates, manager }: Props) => {
  const cssTheme = useTheme();
  const classes = useStyles({ theme: cssTheme });

  const action = useActionMenuDispatch();
  const { router, match } = useRouter();

  const pageInfo = {
    total: delegates.pageInfo?.total ?? 0,
    limit: delegates.pageInfo?.limit ?? 10,
    offset: delegates.pageInfo?.offset ?? 0,
    given: delegates.pageInfo?.given ?? delegates.edges?.length ?? 0
  };

  const currentField = !!match.location?.query?.field
    ? '&field=' + match.location?.query?.field
    : '';

  const currentAscending = !!match.location?.query?.ascending
    ? '&ascending=' + match.location?.query?.ascending
    : '';

  const currentFilter = !!match.location?.query?.filter
    ? '&filter=' + match.location?.query?.filter
    : '';

  const currentSecondaryCat = !!match.location?.query?.secondaryCategory
    ? '&secondaryCategory=' + match.location?.query?.secondaryCategory
    : '';

  const currentHasActiveCert = !!match.location?.query?.hasActiveCert
    ? '&hasActiveCert=' + match.location?.query?.hasActiveCert
    : '';

  const currentHasExpiredCert = !!match.location?.query?.hasExpiredCert
    ? '&hasExpiredCert=' + match.location?.query?.hasExpiredCert
    : '';

  const simulatorCompliant = !!match.location?.query?.simulatorCompliant
    ? '&simulatorCompliant=' + match.location?.query?.simulatorCompliant
    : '';

  const currentSearch = !!match.location?.query?.search
    ? '&search=' + encodeURIComponent(match.location?.query?.search)
    : '';

  const sharedParams = `${currentField}${currentAscending}${currentFilter}${currentSecondaryCat}${currentHasActiveCert}${currentHasExpiredCert}${simulatorCompliant}`;

  const certCategories = (
    manager.company.certificatesStatus?.secondaryCategoryStatuses ?? []
  )
    .map((status) => status?.secondaryCategory)
    .filter(
      (category): category is { uuid: string; name: string } =>
        !!category?.uuid && !!category?.name
    );

  const selectedCategory = match.location?.query?.secondaryCategory ?? '';
  const anyFilterActive =
    !!match.location?.query?.filter ||
    !!selectedCategory ||
    match.location?.query?.hasActiveCert === 'true' ||
    match.location?.query?.hasExpiredCert === 'true';

  const onSearch = useTableSearch((text: string) => {
    const searchField =
      text.length > 0 ? `&search=${encodeURIComponent(text)}` : '';
    return `/app/delegates?offset=0&limit=${
      match?.location?.query?.limit ?? 10
    }${sharedParams}${searchField}`;
  });

  const pushOffset = (offset: number) => {
    router.push(
      `/app/delegates?offset=${Math.max(offset, 0)}&limit=${
        pageInfo.limit
      }${sharedParams}${currentSearch}`
    );
  };
  const onNextPage = () => pushOffset(pageInfo.offset + pageInfo.limit);
  const onPreviousPage = () => pushOffset(pageInfo.offset - pageInfo.limit);

  const onFilterClick = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    action({
      type: 'show',
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'bottom-left'
      },
      children: (
        <DelegatesFilterOptions
          certCategories={certCategories}
          state={{
            allComplete: match.location?.query?.filter === ALL_COMPLETED,
            sixMonths: match.location?.query?.filter === EXPIRING_BEFORE_6,
            threeMonths: match.location?.query?.filter === EXPIRING_BEFORE_3,
            incompleteCourse:
              match.location?.query?.filter === HAS_INCOMPLETE_COURSE,
            hasActiveDocument:
              match.location?.query?.filter === HAS_ACTIVE_DOCUMENT,
            hasExpiredDocument:
              match.location?.query?.filter === HAS_EXPIRED_DOCUMENT,
            hasActiveBackgroundCheck:
              match.location?.query?.filter === HAS_ACTIVE_BACKGROUND_CHECK,
            hasExpiredBackgroundCheck:
              match.location?.query?.filter === HAS_EXPIRED_BACKGROUND_CHECK,
            secondaryCategory: selectedCategory,
            hasActiveCert: match.location?.query?.hasActiveCert === 'true',
            hasExpiredCert: match.location?.query?.hasExpiredCert === 'true'
          }}
          onChange={(newState) => {
            let filterField = '';

            if (newState.allComplete) {
              filterField = `&filter=${ALL_COMPLETED}`;
            } else if (newState.sixMonths) {
              filterField = `&filter=${EXPIRING_BEFORE_6}`;
            } else if (newState.threeMonths) {
              filterField = `&filter=${EXPIRING_BEFORE_3}`;
            } else if (newState.incompleteCourse) {
              filterField = `&filter=${HAS_INCOMPLETE_COURSE}`;
            } else if (newState.hasActiveDocument) {
              filterField = `&filter=${HAS_ACTIVE_DOCUMENT}`;
            } else if (newState.hasExpiredDocument) {
              filterField = `&filter=${HAS_EXPIRED_DOCUMENT}`;
            } else if (newState.hasActiveBackgroundCheck) {
              filterField = `&filter=${HAS_ACTIVE_BACKGROUND_CHECK}`;
            } else if (newState.hasExpiredBackgroundCheck) {
              filterField = `&filter=${HAS_EXPIRED_BACKGROUND_CHECK}`;
            }

            if (newState.secondaryCategory) {
              filterField += `&secondaryCategory=${newState.secondaryCategory}`;
              // Cert status filters only work alongside a category on the backend
              if (newState.hasActiveCert) {
                filterField += '&hasActiveCert=true';
              } else if (newState.hasExpiredCert) {
                filterField += '&hasExpiredCert=true';
              }
            }

            router.push(
              `${window.location.pathname}?limit=${
                match?.location?.query?.limit ?? 10
              }&offset=0${currentAscending}${currentField}${filterField}${currentSearch}`
            );
          }}
        />
      )
    });
  };

  const onSortClick = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    action({
      type: 'show',
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'bottom-left'
      },
      children: (
        <DelegatesSortOptions
          state={{
            nameASC:
              match.location?.query?.field === 'first_name' &&
              match.location?.query?.ascending === 'true',
            nameDESC:
              match.location?.query?.field === 'first_name' &&
              match.location?.query?.ascending === 'false',
            expiringASC:
              match.location?.query?.field === 'expiration_date' &&
              match.location?.query?.ascending === 'true',
            expiringDESC:
              match.location?.query?.field === 'expiration_date' &&
              match.location?.query?.ascending === 'false'
          }}
          onChange={(newState) => {
            let sortField = '';

            if (newState.nameASC) {
              sortField = `&field=first_name&ascending=true`;
            } else if (newState.nameDESC) {
              sortField = `&field=first_name&ascending=false`;
            } else if (newState.expiringASC) {
              sortField = `&field=expiration_date&ascending=true`;
            } else if (newState.expiringDESC) {
              sortField = `&field=expiration_date&ascending=false`;
            }

            router.push(
              `${window.location.pathname}?limit=${
                match?.location?.query?.limit ?? 10
              }&offset=${
                match?.location?.query?.offset ?? 0
              }${sortField}${currentFilter}${currentSearch}`
            );
          }}
        />
      )
    });
  };

  const formatRow: RowCell<DelegateRow>[] = [
    {
      header: 'Name',
      width: '30%',
      format: (delegate) => (
        <UserLabel
          name={`${delegate?.firstName} ${delegate?.lastName}`}
          profileUrl={delegate?.profileImageUrl ?? undefined}
        />
      )
    },
    {
      header: 'Email',
      format: (delegate) => (
        <Text text={delegate?.email ?? ''} color={theme.colors.textBlue} />
      )
    },
    {
      header: 'TTC ID',
      format: (delegate) => <Text text={delegate?.TTC_ID ?? ''} />
    },
    {
      header: 'Next Expiry',
      format: (delegate) => (
        <Text text={delegate?.nextExpiry ?? ''} formatDate />
      )
    }
  ];

  return (
    <div className={classes.root}>
      <PageTitle
        title={manager.company.name}
        subTitle="Organisation Overview"
        sideComponent={
          <div className={classes.headerSide}>
            <div className={classes.headerDivider} />
            <div className={classes.headerSideCol}>
              <p className={classes.headerSideText}>
                {manager.company.numDelegates} members total
              </p>
              {(manager.company?.simulatorCompliance?.activeUsers ?? 0) > 0 && (
                <div
                  className={classes.downloadReport}
                  onClick={() =>
                    window.open(
                      manager?.company?.simulatorReportPreviewURL ?? '',
                      '_blank'
                    )
                  }
                >
                  Download 6x6 report
                </div>
              )}
            </div>
          </div>
        }
      />
      <div className={classes.spacer} />
      <Table
        data={delegates.edges ?? []}
        formatRow={formatRow}
        tableName="delegates"
        pageInfo={pageInfo}
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        onSearch={onSearch}
        searchValue={match.location?.query?.search}
        onFilterClick={onFilterClick}
        filterActive={anyFilterActive}
        onSortClick={onSortClick}
        sortActive={!!match.location?.query?.field}
        onRowClick={(delegate) =>
          delegate && router.push(`/app/delegates/${delegate.uuid}`)
        }
        emptyTableText="No delegates found"
      />
    </div>
  );
};

const DelegatesPageFrag = createFragmentContainer(DelegatesPage, {
  delegates: graphql`
    fragment DelegatesPage_delegates on DelegatePage {
      edges {
        uuid
        TTC_ID
        email
        firstName
        lastName
        profileImageUrl
        createdAt
        nextExpiry
      }
      pageInfo {
        total
        offset
        limit
        given
      }
    }
  `,
  manager: graphql`
    fragment DelegatesPage_manager on Manager {
      company {
        name
        numDelegates
        simulatorReportPreviewURL
        simulatorCompliance {
          activeUsers
        }
        certificatesStatus {
          secondaryCategoryStatuses {
            secondaryCategory {
              uuid
              name
            }
          }
        }
      }
    }
  `
});

export default DelegatesPageFrag;
